import type { AdminSection } from "./components/AdminSections";
import type { MenuItem } from "./MenuData";
import { ProductInternalName } from "./productConfig";
import { isProductionEnvironment } from "./sideMenuEnv";
import { isDemoClient } from "@shared/utils/clientEnv";
import {
	isCustomerApiEnabled,
	isInternetMonitoringEnabled,
	isSubscriptionsManagerEnabled,
	isTagsManagerEnabled,
} from "@shared/config/featureFlags";

/** No app route is registered for `/kontrol/admin-center` alone; use a concrete child path. */
const DEFAULT_KONTROL_ADMIN = "/kontrol/admin-center/datacenter-inventory";

/**
 * Lateral "Admin Center" link target by product. Reflector V2 uses a leading `/` (was missing before).
 * Kontrol/unknown: a real child of `/kontrol/admin-center` (see kontrolEdge/routes).
 * Insight: use `getFirstAvailableAdminRoute` + same fallback in SideMenu (permission-aware), not this helper alone.
 */
export const getAdminCenterPathForProduct = (internalName: string): string => {
	switch (internalName) {
		case ProductInternalName.CtxReflector:
			return "/reflector/admin-center/management/licensing";
		case ProductInternalName.CtxReflectorV2:
			return "/reflector-v2/admin-center/management/licensing";
		default:
			return DEFAULT_KONTROL_ADMIN;
	}
};

export const ADMIN_CENTER_SHELL_PREFIXES: readonly string[] = [
	"/kontrol/admin-center",
	"/reflector-v2/admin-center",
	/** CTX Reflector v1: single admin route today (licensing) — see ctxReflector/routes */
	"/reflector/admin-center",
] as const;

/**
 * When true, show the in-menu Admin Center sheet (Kontrol / Insight / V2) instead of the standard nav.
 */
export const isAdminCenterShellPath = (pathname: string): boolean => {
	const path = pathname.split("?")[0] || "";
	return ADMIN_CENTER_SHELL_PREFIXES.some((p) => path.startsWith(p));
};

export const isKontrolAdminCenterPath = (pathname: string): boolean => {
	const path = pathname.split("?")[0] || "";
	return path.startsWith("/kontrol/admin-center");
};

/*
 * There is no `isSettingsShellPath` any more (CTX-512, CTX-581). The drill-in
 * used to take over the rail on every `/account/*` route, because that is where
 * those screens' navigation lived. They are rows of the Account section now, so
 * the rail must stay on the product nav to keep the active row visible —
 * `isAdminCenterShellPath` above is the only thing that opens a drill-in.
 */

export const getFirstAvailableAdminRoute = (sections: AdminSection[]): string | null => {
	for (const section of sections) {
		if (section.route) {
			return section.route;
		}
		if (section.children && section.children.length > 0) {
			const childRoute = getFirstAvailableAdminRoute(section.children);
			if (childRoute) {
				return childRoute;
			}
		}
	}
	return null;
};

const REPORTS_ROOT_ID = "reports-root";
const KONTROL_ALERTS_ID = "kontrol-alerts";
const KONTROL_INTERNET_IP_MONITORING_ID = "kontrol-internet-ip-monitoring";

/**
 * "Events & Reports" rows for Kontrol (tenant gating, alerts visibility).
 *
 * Internet monitoring shares the Alerts audience rule (`canAccessKontrolAlerts`,
 * i.e. partner view only). `isInternetMonitoringEnabled` is still asked even
 * though it went GA at the CTX-339 cutover and now returns true: it is the one
 * switch that turns the feature off in a hurry, and the route guard asks the
 * same pair, so the menu can't advertise a page the router refuses — see
 * `KontrolInternetMonitoringRoute`.
 */
export const filterKontrolEventManagementItems = (
	items: MenuItem[],
	tenantType: string | undefined,
	showKontrolAlertsMenu: boolean
): MenuItem[] => {
	return items.filter((item) => {
		if (item.id === REPORTS_ROOT_ID) {
			return tenantType === "PARTNER" || tenantType === "GLOBAL_ADMIN";
		}
		if (item.id === KONTROL_ALERTS_ID) {
			return showKontrolAlertsMenu;
		}
		if (item.id === KONTROL_INTERNET_IP_MONITORING_ID) {
			return showKontrolAlertsMenu && isInternetMonitoringEnabled();
		}
		return true;
	});
};

/**
 * Main section: Kontrol is Overview-only; other products use `defaultItems`.
 */
export const getMainSectionItemsForProduct = (
	internalName: string,
	defaultItems: MenuItem[],
	kontrolEdgeMain: MenuItem[]
): MenuItem[] => {
	if (internalName === ProductInternalName.KontrolEdge) {
		return kontrolEdgeMain;
	}
	return defaultItems;
};

/** Identifier strip rendered under the tenant card's `Partner › Customer` breadcrumb. */
export type TenantCardSubLine = {
	text: string;
	/** Brand (teal) strip for the partner identifier, muted gray for a customer. */
	brand: boolean;
};

/**
 * Which identifier the tenant card shows under the breadcrumb, or `null` when the
 * tenant has nothing to show there.
 *
 * The partner identifier is only reachable for `GLOBAL_ADMIN`: it comes from the
 * `/tenant/partners` payload, which `usePartnerCustomerSelection` fetches for that
 * tenant type alone. A PARTNER in partner view would therefore render a permanent
 * `Partner id: —` placeholder, so the strip is dropped entirely for them. Customer
 * identifiers come from `/tenant/customers`, which every tenant that renders the
 * card does receive, so those stay.
 *
 * Shared by the desktop dropdown and the mobile bottom-sheet variants of
 * `SideMenuTenantCard` so the two surfaces cannot drift apart.
 */
export const getTenantCardSubLine = ({
	tenantType,
	inPartnerView,
	partnerCode,
	customerId,
}: {
	tenantType: string | undefined;
	inPartnerView: boolean;
	partnerCode: string | null | undefined;
	customerId: string | null | undefined;
}): TenantCardSubLine | null => {
	if (inPartnerView) {
		if (tenantType !== "GLOBAL_ADMIN") return null;
		return { text: `Partner id: ${partnerCode ?? "—"}`, brand: true };
	}
	return { text: `Customer id: ${customerId ?? "—"}`, brand: false };
};

/**
 * Whether the tenant card (the `Partner › Customer` breadcrumb) renders at all.
 *
 * A CUSTOMER tenant has no scope to switch — it *is* the leaf — so the card is
 * absent for them, and it is hidden in the collapsed rail.
 *
 * Extracted and exported for the chrome above, which briefly sized its seam from
 * this answer. That seam is now unconditional (see `CHROME_SEAM_ABOVE_NAV`), so
 * the card is the only caller again — the predicate is kept because it names and
 * tests a real rule rather than because a second surface needs it.
 */
export const shouldShowTenantCard = ({
	tenantType,
	collapsed = false,
}: {
	tenantType: string | undefined;
	collapsed?: boolean;
}): boolean => !collapsed && tenantType !== "CUSTOMER";

/**
 * Human label for a tenant type. Kept as a map rather than a chain of ternaries
 * so an unknown value from the backend falls through to `undefined` and the
 * caller decides, instead of being silently labelled as one of the known types.
 */
const TENANT_TYPE_LABEL: Readonly<Record<string, string>> = {
	GLOBAL_ADMIN: "Global admin",
	PARTNER: "Partner",
	CUSTOMER: "Customer",
};

/**
 * Secondary line of the account block in the switcher dropdown — "Partner · CCC.123"
 * (CTX-335).
 *
 * Distinct from `getTenantCardSubLine` and deliberately not merged with it: that
 * one describes the *scope the user is currently browsing*, which moves as they
 * switch partner or customer, while this describes **who the logged-in user is**,
 * which does not. They read similarly on screen and would drift into each other
 * if one helper tried to serve both.
 *
 * Returns `null` rather than a partial string when the tenant type is unknown:
 * a bare "· CCC.123" with no label reads as a rendering bug.
 */
export const getAccountScopeLine = ({
	tenantType,
	tenantCode,
}: {
	tenantType: string | undefined;
	tenantCode: string | null | undefined;
}): string | null => {
	const label = TENANT_TYPE_LABEL[tenantType ?? ""];
	if (!label) return null;
	return tenantCode ? `${label} · ${tenantCode}` : label;
};

/**
 * Source of truth for who can access the Notifications module
 * (`/account/notifications` and its `/groups/*` sub-routes).
 *
 * Shared by:
 *   - the sidemenu gate (`filterAccountAreaItems`) so the entry
 *     is hidden for unauthorized tenants, and
 *   - the route guard (`RequireTenantType` in `src/settings/routes.tsx`)
 *     so direct URL access is also blocked.
 *
 * Both layers MUST use this helper — keeping a single rule prevents the
 * sidemenu and the router from drifting.
 *
 * Rule:
 *   - Production build for a regular client → `GLOBAL_ADMIN` only.
 *   - Staging / dev / `REACT_APP_CLIENT=demo` (any build) → `GLOBAL_ADMIN`
 *     and `PARTNER`. The demo client preserves the staging behavior so
 *     PARTNER tenants can showcase the feature.
 *   - `CUSTOMER` is never allowed.
 */
export const getNotificationsAllowedTenantTypes = (): string[] => {
	const isProd = isProductionEnvironment();
	const isDemo = isDemoClient();
	if (isProd && !isDemo) {
		return ["GLOBAL_ADMIN"];
	}
	return ["GLOBAL_ADMIN", "PARTNER"];
};

/**
 * Source of truth for who can access Subscriptions management
 * (`/kontrol/admin-center/subscriptions`).
 *
 * Shared by:
 *   - the Admin Center menu gate (`filterSubscriptionsAdminSection`), and
 *   - the route guard (`RequireTenantType` in `src/kontrolEdge/routes.tsx`),
 * so direct URL access can't bypass the hidden menu entry. Both layers MUST
 * go through this helper — same contract as
 * `getNotificationsAllowedTenantTypes()`.
 *
 * Rule today: `GLOBAL_ADMIN` only, in every environment.
 *
 * Opening this to partners later is a one-line change here: the backend
 * already scopes subscriptions by tenant hierarchy, and the UI reads through
 * `useEffectiveTenantId`, so a PARTNER would automatically see their own
 * tree rather than the whole system. Do NOT re-inline the role check at any
 * call site, or the two layers will drift.
 *
 * Note this is orthogonal to `isSubscriptionsManagerEnabled()`: that flag
 * decides whether the feature exists in this build at all, this decides who
 * may use it where it exists.
 */
export const getSubscriptionsAllowedTenantTypes = (): string[] => [
	"GLOBAL_ADMIN",
];

/** Id of the Admin Center section that holds subscriptions management. */
export const SUBSCRIPTIONS_ADMIN_SECTION_ID = "subscriptions";

/**
 * Keep or drop the Subscriptions section of the Admin Center.
 *
 * Two independent reasons to hide it, both checked here so no call site has to
 * remember them:
 *   - the feature is WIP and ships hidden in production
 *     (`isSubscriptionsManagerEnabled`);
 *   - it is restricted to `getSubscriptionsAllowedTenantTypes()`.
 *
 * The tenant-type check is NOT redundant with the Admin Center's own gate. That
 * gate is `tenantType === "GLOBAL_ADMIN" || isEmailInAdminCenterWhitelist(...)`,
 * so a whitelisted email that is not a Global Admin can open the Admin Center —
 * and would see a section whose API answers 403. Filtering here keeps the menu
 * honest about what the backend will actually serve.
 *
 * @param sections Admin Center sections for the active product.
 * @param tenantType Caller's tenant type.
 * @param isProduction Passed in rather than resolved here — `REACT_APP_*` reads
 * are inlined at build time, which makes the default path untestable.
 */
export const filterSubscriptionsAdminSection = (
	sections: AdminSection[],
	tenantType: string | undefined,
	isProduction: boolean
): AdminSection[] => {
	const allowed =
		isSubscriptionsManagerEnabled(isProduction) &&
		tenantType !== undefined &&
		getSubscriptionsAllowedTenantTypes().includes(tenantType);

	if (allowed) {
		return sections;
	}
	return sections.filter(
		(section) => section.id !== SUBSCRIPTIONS_ADMIN_SECTION_ID
	);
};

/**
 * Who may manage the Carrier Access of an Extended PoP from the Admin Center
 * (CTX-830): declare the 84-, change its legs and capture each leg's medium.
 * Applied by the menu (`filterExtenderPopCarrierAccessAdminSection`) and the
 * route guard (`RequireTenantType` in `src/kontrolEdge/routes.tsx`) — the
 * backend answers 403 to anything that is not a Global Admin.
 */
export const getExtenderPopCarrierAccessAllowedTenantTypes = (): string[] => [
	"GLOBAL_ADMIN",
];

/** Id of the Admin Center section that manages Extended PoP Carrier Access. */
export const EXTENDER_POP_CARRIER_ACCESS_ADMIN_SECTION_ID =
	"extenderPopCarrierAccess";

/**
 * Keep or drop the Extended PoP Carrier Access section of the Admin Center.
 * Hidden in production while Extended PoP is BETA, and restricted to
 * `getExtenderPopCarrierAccessAllowedTenantTypes()` for the same reason as
 * `filterSubscriptionsAdminSection`: a whitelisted email that is not a Global
 * Admin can open the Admin Center, and must not see a section whose API
 * refuses it.
 */
export const filterExtenderPopCarrierAccessAdminSection = (
	sections: AdminSection[],
	tenantType: string | undefined,
	isProduction: boolean
): AdminSection[] => {
	const allowed =
		!isProduction &&
		tenantType !== undefined &&
		getExtenderPopCarrierAccessAllowedTenantTypes().includes(tenantType);

	if (allowed) {
		return sections;
	}
	return sections.filter(
		(section) => section.id !== EXTENDER_POP_CARRIER_ACCESS_ADMIN_SECTION_ID
	);
};

/**
 * Single source of truth for who sees each row of the rail's Account section
 * (`accountAreaItems` in `MenuData.ts`).
 *
 * Per-item rules:
 *   - `account-management-customers`: GLOBAL_ADMIN only, any environment.
 *   - `account-management-tags`: gated behind the Tags Manager flag
 *     (`REACT_APP_ENABLE_TAGS_MANAGER`, see `isTagsManagerEnabled()`). When
 *     enabled it is visible to every tenant type (CUSTOMER / PARTNER /
 *     GLOBAL_ADMIN) in every environment — tags are tenant-scoped at the
 *     backend so no further role gating is required. When disabled the entry
 *     is hidden and the legacy tag flow is used.
 *   - `account-management-notifications`: governed by
 *     `getNotificationsAllowedTenantTypes()` — GLOBAL_ADMIN only in prod,
 *     GLOBAL_ADMIN + PARTNER in staging / dev / demo. Kept in sync with the
 *     route guard in `src/settings/routes.tsx` through that helper.
 *   - `account-management-integrations` (Customer API tokens, `/account/api`):
 *     WIP — governed by `isCustomerApiEnabled()`, which the route registration
 *     in `src/settings/routes.tsx` reads too. Hidden in production for every
 *     tenant, GLOBAL_ADMIN-only elsewhere. One predicate on purpose: this row
 *     was gated and its route was not, which left the page reachable by direct
 *     URL in production (CTX-37).
 *   - `support-audit-logs`: BETA / WIP — hidden in production for every tenant,
 *     GLOBAL_ADMIN-only elsewhere. The `demo` client (`REACT_APP_CLIENT=demo`)
 *     bypasses the prod gate for this one, because
 *     demo.transitxchange.cloud is a showcase surface, not a release to real
 *     tenants.
 *   - everything else (Profile, Users): always shown.
 *
 * Until CTX-512 this lived in two helpers — `filterAccountAreaItems` for the
 * flat rows and `filterAccountSettingsChildren` for the two nested under an
 * `account-management` parent — which restated three of these rules twice. The
 * section is one flat list now, so it gets one filter; keep it that way rather
 * than re-inlining a role check at a call site.
 *
 * @param items Rows to filter, normally `accountAreaItems`.
 * @param tenantType Caller's tenant type.
 * @param isProduction Passed in rather than resolved here — `REACT_APP_*` reads
 * are inlined at build time, which makes the default path untestable.
 */
export const filterAccountAreaItems = (
	items: MenuItem[],
	tenantType: string | undefined,
	isProduction: boolean
): MenuItem[] => {
	const isDemo = isDemoClient();
	return items.filter((item) => {
		switch (item.id) {
			case "account-management-customers":
				return tenantType === "GLOBAL_ADMIN";
			case "account-management-tags":
				return isTagsManagerEnabled();
			case "account-management-notifications": {
				const allowed = getNotificationsAllowedTenantTypes();
				return tenantType !== undefined && allowed.includes(tenantType);
			}
			case "account-management-integrations":
				return isCustomerApiEnabled(isProduction) && tenantType === "GLOBAL_ADMIN";
			case "support-audit-logs":
				if (isProduction && !isDemo) {
					return false;
				}
				return tenantType === "GLOBAL_ADMIN";
			default:
				return true;
		}
	});
};

/**
 * The same rows, grouped as the rail renders them (`accountAreaSections`).
 *
 * Delegates every per-row decision to `filterAccountAreaItems` — the grouping
 * is presentation and carries no rules of its own, so this cannot drift from
 * the flat filter. A group whose rows are all gated out is dropped rather than
 * left as a header standing over nothing; a lone row (API) is kept or dropped
 * on its own.
 *
 * @param sections Grouped rows, normally `accountAreaSections`.
 * @param tenantType Caller's tenant type.
 * @param isProduction Passed in for the same reason as above.
 */
export const filterAccountAreaSections = (
	sections: MenuItem[],
	tenantType: string | undefined,
	isProduction: boolean
): MenuItem[] =>
	sections.reduce<MenuItem[]>((kept, section) => {
		if (!section.children) {
			const [row] = filterAccountAreaItems([section], tenantType, isProduction);
			return row ? [...kept, row] : kept;
		}

		const children = filterAccountAreaItems(
			section.children,
			tenantType,
			isProduction
		);
		return children.length > 0 ? [...kept, { ...section, children }] : kept;
	}, []);
